import { Theme } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { Box } from "@mui/system";
import { BarDatum, ComputedDatum, ResponsiveBar } from "@nivo/bar";
import { OrdinalColorScaleConfig } from "@nivo/colors";
import { thousandSeparateNumber } from "../dashboard/DashboardWidgetService";

export type BarChartProps = {
    colors: OrdinalColorScaleConfig<ComputedDatum<BarDatum>> | undefined;
    data: BarDatum[];
    digitsForLeftMargin?: number;
    keys: string[];
    unit?: string;
    indexBy: string;
    padding?: number;
    innerPadding?: number;
    xAxisTitle?: string;
    yAxisTitle?: string;
    tickValues?: number | number[];
    // Set this when every value is 0, otherwise the y domain collapses to [0, 0] and the 0 line ends up mid chart.
    maxValue?: number;
    // Keep this a fixed number of pixels. ResponsiveBar measures its container, so a percentage height on the
    // element it measures makes the container depend on the chart and the chart on the container, and its
    // ResizeObserver loops. Use fill instead to take up whatever height is available.
    height?: number;
    // Fills the height of the nearest positioned ancestor instead of standing at a fixed height, so the chart uses
    // the whole content area of its widget rather than leaving a gap under the x axis.
    fill?: true;
    seriesLabels?: { [key: string]: string };
};

const yAxisSpacingForDigits = (digits: number) => (digits <= 1 ? 25 : 35 + (digits - 2) * 7);

const defaultChartHeight = 280;

function BarChart(props: BarChartProps) {
    const calculateLeftMargin = () => {
        let leftMargin = yAxisSpacingForDigits(props.digitsForLeftMargin || 1);

        if (props.yAxisTitle) leftMargin += 25;

        return leftMargin;
    };

    const calculateYAxisOffset = () => (yAxisSpacingForDigits(props.digitsForLeftMargin || 1) + 20) * -1;

    const styles = makeStyles((theme: Theme) =>
        createStyles({
            barContainer: {
                width: "100%",
                "& g rect": {
                    cursor: "pointer",
                },
            },
        })
    );

    // In fill mode the measured box is positioned absolutely, so its size comes only from the ancestor it stretches
    // to and never from the chart drawn inside it. That is what keeps the ResizeObserver from looping.
    const measuredBoxSx = props.fill ? { position: "absolute" as const, top: 0, right: 0, bottom: 0, left: 0 } : { height: props.height ?? defaultChartHeight };

    const chart = (
        <Box className={styles().barContainer} sx={() => measuredBoxSx}>
            <ResponsiveBar
                axisBottom={{
                    tickSize: 0,
                    tickPadding: 10,
                    legend: props.xAxisTitle ? props.xAxisTitle : props.indexBy.charAt(0).toUpperCase() + props.indexBy.slice(1),
                    legendPosition: "middle",
                    legendOffset: 40,
                }}
                axisLeft={{
                    format: (yTick) => thousandSeparateNumber(Number.parseInt(yTick, 10)),
                    tickPadding: 15,
                    tickRotation: 0,
                    tickSize: 0,
                    tickValues: props.tickValues ? props.tickValues : 6,
                    legend: props.yAxisTitle,
                    legendPosition: "middle",
                    legendOffset: calculateYAxisOffset(),
                }}
                gridYValues={props.tickValues ? props.tickValues : 6}
                colors={props.colors}
                data={props.data}
                enableLabel={false}
                groupMode="grouped"
                indexBy={props.indexBy}
                innerPadding={props.innerPadding ?? 10}
                keys={props.keys}
                minValue={0}
                maxValue={props.maxValue ?? "auto"}
                margin={{
                    top: 10,
                    right: 0,
                    bottom: 50,
                    left: calculateLeftMargin(),
                }}
                padding={props.padding ?? 0.75}
                theme={{
                    fontFamily: "Open Sans",
                    fontSize: 12,
                    textColor: "#000000",
                    axis: { ticks: { text: { fill: "#707070" } } },
                    grid: { line: { stroke: "#D9D3CC" } },
                    tooltip: {
                        container: { borderRadius: 10 },
                        basic: { fontWeight: "normal" },
                    },
                }}
                tooltipLabel={(data) => {
                    const tooltip = data.id.toString();
                    return props.seriesLabels?.[tooltip] ?? `${tooltip.charAt(0).toUpperCase() + tooltip.slice(1)}`;
                }}
                valueFormat={(value: number) => `${thousandSeparateNumber(value)} ${props.unit || ""}`}
            />
        </Box>
    );

    if (!props.fill) return chart;

    // Only the absolutely positioned child lives in here, so this wrapper's height is decided by its parent alone.
    // minHeight keeps the chart visible if the parent turns out to have no height of its own to give.
    return <Box sx={{ position: "relative", width: "100%", height: "100%", minHeight: defaultChartHeight }}>{chart}</Box>;
}

export default BarChart;
